import React, { useEffect, useState } from "react";
import MainLayout from "../../Layout/mainLayout";
import { Link, useNavigate, useParams } from "react-router-dom";
import "./viewPartner.css";
import {
    AgentListView,
} from "../../Redux/Actions/agentActions";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import HierarchyChain from "../Franchise Manager/hierarchyChain";
import ViewFranchiseManagerHistory from "../Franchise Manager/viewFranchiseManagerHistory";

const formatDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return "—";
    }
};

const docStatusLabel = (status) => {
    if (status === "approved") return "Verified";
    if (status === "rejected") return "Rejected";
    return "Pending";
};

const ViewPartner = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();

    const [showHistory, setShowHistory] = useState(false);

    const agentState = useSelector((state) => state.agents || {});
    const detailLoading = agentState.loading || false;

    useEffect(() => {
        if (id) {
            dispatch(AgentListView(id)).catch(() => {
                toast.error("Failed to load partner details");
            });
        }
    }, [id, dispatch]);

    const data = agentState.agentView?.data;

    const getInitials = (name) => {
        if (!name) return "PT";
        const parts = name.trim().split(" ");
        return parts.length > 1
            ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
            : name.slice(0, 2).toUpperCase();
    };


    if (detailLoading && !data) {
        return (
            <MainLayout>
                <div className="vpd-wrapper">
                    <div className="vpd-loading">
                        <div className="vpd-spinner-lg" />
                        <span>Loading partner...</span>
                    </div>
                </div>
            </MainLayout>
        );
    }

    if (!detailLoading && !data) {
        return (
            <MainLayout>
                <div className="vpd-wrapper">
                    <div className="vpd-empty-state">
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                        <h3>Partner not found</h3>
                        <Link to="/franchise" className="vpd-btn vpd-btn--primary">
                            Back to list
                        </Link>
                    </div>
                </div>
            </MainLayout>
        );
    }

    const documentList = data.document_list || [];

    const derivedDocStatus =
        documentList.length === 0
            ? null
            : documentList.every((d) => d.isVerified)
                ? "approved"
                : documentList.some((d) => d.status === "rejected")
                    ? "rejected"
                    : "pending";

    const cityHeadNames = data.city_head_id?.map((ch) => ch.name).join(", ");
    const localityHeadNames = data.locality_head
        ?.map((lh) => lh.locality_head?.name)
        .filter(Boolean)
        .join(", ");

    return (
        <MainLayout>
            <div className="vpd-wrapper">
                {/* Header */}
                <div className="vpd-header">
                    <div className="vpd-header-left">
                        <button className="vpd-back-btn" onClick={() => navigate(-1)} type="button" aria-label="Go back">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="15 18 9 12 15 6" />
                            </svg>
                        </button>
                        <div>
                            <h1 className="vpd-title">Partner Details</h1>
                            <p className="vpd-subtitle">View complete profile information</p>
                        </div>
                    </div>

                    <div className="vpd-header-actions">
                        {data.history?.length > 0 && (
                            <button className="vpd-history-btn" onClick={() => setShowHistory(true)} type="button">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10" />
                                    <polyline points="12 6 12 12 16 14" />
                                </svg>
                                History
                            </button>
                        )}

                        <Link to={`/agent/edit/${id}`} className="vpd-edit-btn">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                            Edit
                        </Link>
                    </div>
                </div>

                {/* Profile summary card */}
                <div className="vpd-profile-card">
                    <div className="vpd-avatar">
                        {data.profile_image ? (
                            <img src={data.profile_image} alt={data.name} className="vpd-avatar-img" />
                        ) : (
                            <span className="vpd-avatar-initials">{getInitials(data.name)}</span>
                        )}
                    </div>

                    <div className="vpd-profile-info">
                        <div className="vpd-profile-name-row">
                            <h2 className="vpd-profile-name">{data.name || "—"}</h2>
                            <span className={`vpd-status-badge vpd-status-badge--${data.status === "active" ? "active" : "inactive"}`}>
                                <span className="vpd-status-dot" />
                                {data.status === "active" ? "Active" : "Inactive"}
                            </span>
                        </div>
                        <p className="vpd-profile-role">{data.role || "—"}</p>

                        <div className="vpd-profile-meta">
                            <span className="vpd-profile-meta-item">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M22 6l-10 7L2 6" />
                                    <path d="M2 6h20v12H2z" />
                                </svg>
                                {data.email || "—"}
                                {data.email && (
                                    <span className={`vpd-verify-badge ${data.is_email_verified ? "vpd-verify-badge--yes" : "vpd-verify-badge--no"}`}>
                                        {data.is_email_verified ? "Verified" : "Unverified"}
                                    </span>
                                )}
                            </span>
                            <span className="vpd-profile-meta-item">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                                </svg>
                                {data.country_code} {data.contact_number || "—"}
                                {data.contact_number && (
                                    <span className={`vpd-verify-badge ${data.is_contact_number_verified ? "vpd-verify-badge--yes" : "vpd-verify-badge--no"}`}>
                                        {data.is_contact_number_verified ? "Verified" : "Unverified"}
                                    </span>
                                )}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Partner info */}
                <div className="vpd-section-card">
                    <div className="vpd-section-header">
                        <span className="vpd-section-badge">01</span>
                        <h3 className="vpd-section-title">Partner Information</h3>
                    </div>

                    <div className="vpd-detail-grid">
                        <div className="vpd-detail-item">
                            <span className="vpd-detail-label">Agent ID</span>
                            <span className="vpd-detail-value">{data.staff_id || "—"}</span>
                        </div>
                        <div className="vpd-detail-item">
                            <span className="vpd-detail-label">Category</span>
                            <span className="vpd-detail-value">{data.category || "—"}</span>
                        </div>
                        <div className="vpd-detail-item">
                            <span className="vpd-detail-label">Profile Status</span>
                            <span className="vpd-detail-value">{data.profile_status || "—"}</span>
                        </div>
                        <div className="vpd-detail-item">
                            <span className="vpd-detail-label">Account Status</span>
                            <span className="vpd-detail-value">{data.account_status || "—"}</span>
                        </div>
                        <div className="vpd-detail-item">
                            <span className="vpd-detail-label">Joined On</span>
                            <span className="vpd-detail-value">{formatDate(data.created_at)}</span>
                        </div>
                        {data.is_business && (
                            <>
                                <div className="vpd-detail-item">
                                    <span className="vpd-detail-label">Organization</span>
                                    <span className="vpd-detail-value">{data.org_name || "—"}</span>
                                </div>
                                <div className="vpd-detail-item">
                                    <span className="vpd-detail-label">PAN</span>
                                    <span className="vpd-detail-value">{data.org_pan_no || "—"}</span>
                                </div>
                                <div className="vpd-detail-item">
                                    <span className="vpd-detail-label">GST</span>
                                    <span className="vpd-detail-value">{data.org_gst_no || "—"}</span>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Hierarchy */}
                <div className="vpd-section-card">
                    <div className="vpd-section-header">
                        <span className="vpd-section-badge">02</span>
                        <h3 className="vpd-section-title">Hierarchy</h3>
                    </div>
                    <HierarchyChain
                        nodes={[
                            { label: "Franchise Manager", name: data.franchise_id?.name },
                            { label: "City Head", name: cityHeadNames },
                            { label: "Locality Head", name: localityHeadNames },
                            { label: "Partner", name: data.name, active: true },
                        ]}
                    />
                </div>

                {/* Location */}
                <div className="vpd-section-card">
                    <div className="vpd-section-header">
                        <span className="vpd-section-badge">03</span>
                        <h3 className="vpd-section-title">Location & Access</h3>
                    </div>

                    <div className="vpd-tag-group">
                        <span className="vpd-tag-group-label">City</span>
                        <div className="vpd-tag-list">
                            {data.cities?.length > 0 ? (
                                data.cities.map((c) => (
                                    <span className="vpd-tag vpd-tag--city" key={c._id}>
                                        {c.city_name}
                                    </span>
                                ))
                            ) : (
                                <span className="vpd-detail-value">—</span>
                            )}
                        </div>
                    </div>

                    <div className="vpd-tag-group">
                        <span className="vpd-tag-group-label">Locality</span>
                        <div className="vpd-tag-list">
                            {data.locality_head?.length > 0 ? (
                                data.locality_head.map((lh) => (
                                    <span className="vpd-tag vpd-tag--locality" key={lh._id}>
                                        {lh.locality?.name}
                                    </span>
                                ))
                            ) : (
                                <span className="vpd-detail-value">—</span>
                            )}
                        </div>
                    </div>
                </div>

                {/* KYC Documents */}
                {documentList.length > 0 && (
                    <div className="vpd-section-card">
                        <div className="vpd-section-header">
                            <span className="vpd-section-badge">04</span>
                            <h3 className="vpd-section-title">KYC Documents</h3>
                            {derivedDocStatus && (
                                <span className={`vpd-doc-status vpd-doc-status--${derivedDocStatus}`}>{derivedDocStatus}</span>
                            )}
                        </div>

                        {derivedDocStatus === "rejected" && data.document_rejection_reason && (
                            <div className="vpd-doc-rejection">
                                <span className="vpd-detail-label">Rejection Reason</span>
                                <p>{data.document_rejection_reason}</p>
                            </div>
                        )}

                        <div className="vpd-doc-list">
                            {documentList.map((doc) => {
                                const status = doc.status || (doc.isVerified ? "approved" : "pending");
                                const hasImages = doc.frontSide || doc.backSide;
                                return (
                                    <div className="vpd-doc-card" key={doc.id}>
                                        <div className="vpd-doc-card-main">
                                            <div className="vpd-doc-card-heading">
                                                <span className="vpd-doc-card-name">{doc.name}</span>
                                                <span className={`vpd-verify-badge vpd-verify-badge--${status}`}>
                                                    {docStatusLabel(status)}
                                                </span>
                                            </div>
                                            <span className="vpd-doc-card-value">{doc.value || "—"}</span>
                                            {doc.reason && <p className="vpd-doc-card-reason">{doc.reason}</p>}
                                        </div>

                                        <div className="vpd-doc-card-side">
                                            {hasImages ? (
                                                <div className="vpd-doc-card-images">
                                                    {doc.frontSide && (
                                                        <a href={doc.frontSide} target="_blank" rel="noreferrer">
                                                            <img src={doc.frontSide} alt={`${doc.name} Front`} />
                                                        </a>
                                                    )}
                                                    {doc.requiresBoth && doc.backSide && (
                                                        <a href={doc.backSide} target="_blank" rel="noreferrer">
                                                            <img src={doc.backSide} alt={`${doc.name} Back`} />
                                                        </a>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="vpd-doc-card-no-image">No file uploaded</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <ViewFranchiseManagerHistory open={showHistory} onClose={() => setShowHistory(false)} history={data.history} />

            </div>
        </MainLayout>
    );
};

export default ViewPartner;